import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";

const AUTO_COLLAPSE_MS = 6000;

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const v = localStorage.getItem("le17_cookies");
    if (!v) setVisible(true);
  }, []);

  // Auto-collapse to a small pill after a delay, only the first time it shows.
  useEffect(() => {
    if (!visible || collapsed) return;
    timerRef.current = window.setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [visible, collapsed]);

  const set = (choice) => {
    localStorage.setItem("le17_cookies", choice);
    setVisible(false);
  };

  if (!visible) return null;

  // Collapsed pill — small floating button bottom-left.
  if (collapsed) {
    return (
      <button
        data-testid="cookie-pill"
        onClick={() => setCollapsed(false)}
        aria-label={t("cookies.title")}
        title={t("cookies.title")}
        className="fixed bottom-20 lg:bottom-6 left-4 lg:left-6 z-50 w-12 h-12 rounded-full bg-[#0A0A0A] border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center shadow-xl hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all duration-300"
      >
        <Cookie size={20} />
      </button>
    );
  }

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-20 lg:bottom-6 left-4 right-4 lg:left-6 lg:right-auto lg:max-w-md z-50 bg-[#0A0A0A] border border-[#D4AF37]/30 p-6 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <button
        data-testid="cookie-minimize"
        onClick={() => setCollapsed(true)}
        aria-label="minimize"
        className="absolute top-3 right-3 text-[#F8F5EF]/50 hover:text-[#D4AF37] transition-colors"
      >
        <X size={16} />
      </button>
      <div className="overline mb-2 pr-6">{t("cookies.title")}</div>
      <p className="text-sm text-[#F8F5EF]/80 leading-relaxed mb-4">{t("cookies.text")}</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid="cookie-accept" onClick={() => set("accepted")} className="btn-gold text-[10px]">{t("cookies.accept")}</button>
        <button data-testid="cookie-refuse" onClick={() => set("refused")} className="btn-ghost-gold text-[10px]">{t("cookies.refuse")}</button>
        <button data-testid="cookie-customize" onClick={() => set("custom")} className="btn-ghost-gold text-[10px]">{t("cookies.customize")}</button>
      </div>
    </div>
  );
}
